import {
  SubprojectBuildingDetails,
  useSubprojectStore,
} from '@/stores/SubprojectStore';
import { QueryClient } from '@tanstack/react-query';
import { syncBuildingParamsAcrossAlternatives } from './utils';

/**
 * Safely save subproject changes by:
 * 1. Fetching the latest data from the server
 * 2. Merging only the specific changes
 * 3. Saving the merged result
 * 4. Invalidating React Query cache to update UI
 *
 * This prevents data loss when multiple technology pages are being edited.
 */
export async function safelySaveSubproject(
  subprojectId: string,
  organizationId: string,
  localChanges: SubprojectBuildingDetails,
  queryClient?: QueryClient,
): Promise<SubprojectBuildingDetails> {
  const subprojectStore = useSubprojectStore.getState();

  // Step 1: Fetch the absolute latest data from the server
  const latestSubproject = await subprojectStore.fetchSubprojectDetails(
    subprojectId,
    organizationId,
  );

  // Step 2: Merge our local changes into the latest data
  // This preserves changes made in other tabs/pages
  // Helper function to deep merge technology params (category by category)
  const mergeTechnologyParams = (
    latest: Record<string, any>,
    changes: Record<string, any> | undefined,
  ) => {
    if (!changes) return latest;

    const merged = { ...latest };
    // Merge each technology category individually to preserve unmodified categories
    Object.keys(changes).forEach((key) => {
      merged[key] = changes[key];
    });
    return merged;
  };

  const mergedSubproject: SubprojectBuildingDetails = {
    ...latestSubproject,
    // Merge each alternative's technology params
    conventionalAlternative: {
      ...latestSubproject.conventionalAlternative,
      technologyParams: mergeTechnologyParams(
        latestSubproject.conventionalAlternative.technologyParams,
        localChanges.conventionalAlternative?.technologyParams,
      ),
      buildingParams:
        localChanges.conventionalAlternative?.buildingParams ??
        latestSubproject.conventionalAlternative.buildingParams,
      onSiteEnergy:
        localChanges.conventionalAlternative?.onSiteEnergy ??
        latestSubproject.conventionalAlternative.onSiteEnergy,
      externalEnergy:
        localChanges.conventionalAlternative?.externalEnergy ??
        latestSubproject.conventionalAlternative.externalEnergy,
    },
    reducedCarbonAlternative: {
      ...latestSubproject.reducedCarbonAlternative,
      technologyParams: mergeTechnologyParams(
        latestSubproject.reducedCarbonAlternative.technologyParams,
        localChanges.reducedCarbonAlternative?.technologyParams,
      ),
      buildingParams:
        localChanges.reducedCarbonAlternative?.buildingParams ??
        latestSubproject.reducedCarbonAlternative.buildingParams,
      onSiteEnergy:
        localChanges.reducedCarbonAlternative?.onSiteEnergy ??
        latestSubproject.reducedCarbonAlternative.onSiteEnergy,
      externalEnergy:
        localChanges.reducedCarbonAlternative?.externalEnergy ??
        latestSubproject.reducedCarbonAlternative.externalEnergy,
    },
    zeroCarbonAlternative: {
      ...latestSubproject.zeroCarbonAlternative,
      technologyParams: mergeTechnologyParams(
        latestSubproject.zeroCarbonAlternative.technologyParams,
        localChanges.zeroCarbonAlternative?.technologyParams,
      ),
      buildingParams:
        localChanges.zeroCarbonAlternative?.buildingParams ??
        latestSubproject.zeroCarbonAlternative.buildingParams,
      onSiteEnergy:
        localChanges.zeroCarbonAlternative?.onSiteEnergy ??
        latestSubproject.zeroCarbonAlternative.onSiteEnergy,
      externalEnergy:
        localChanges.zeroCarbonAlternative?.externalEnergy ??
        latestSubproject.zeroCarbonAlternative.externalEnergy,
    },
    // Preserve other fields from local changes
    buildingUsages:
      localChanges.buildingUsages ?? latestSubproject.buildingUsages,
    airExchangeFactor:
      localChanges.airExchangeFactor ?? latestSubproject.airExchangeFactor,
    enabledTechnologyCategories:
      localChanges.enabledTechnologyCategories ??
      latestSubproject.enabledTechnologyCategories,
  };

  // Step 3: Sync building params across alternatives
  const syncedSubproject =
    syncBuildingParamsAcrossAlternatives(mergedSubproject);

  // Step 4: Save the merged result (backend will calculate and persist stage statuses)
  const result =
    await subprojectStore.updateSubprojectBuildingDetails(syncedSubproject);

  // Step 5: Invalidate React Query cache AFTER save completes to update UI (progress bars, etc.)
  // Force refetch all queries to ensure progress bars update immediately
  if (queryClient) {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['subproject', subprojectId],
        refetchType: 'all',
      }),
      queryClient.invalidateQueries({
        queryKey: ['projects'],
        refetchType: 'all',
      }),
      queryClient.invalidateQueries({
        queryKey: ['projects-with-subprojects'],
        refetchType: 'all',
      }),
    ]);
  }

  return result;
}
